import { Controller } from "@hotwired/stimulus"
import { loadChart } from "../lib/load_chart"

export default class extends Controller {
  static values = {
    labels: Array,
    datasets: Object,
    currency: String,
    // Series names for the legend; pages that pass none keep the English ones
    names: { type: Object, default: { revenue: "Revenue", expenses: "Expenses", profit: "Profit" } }
  }

  async connect() {
    this.Chart = await loadChart()
    if (this.element.isConnected) this.renderChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  renderChart() {
    const ctx = this.element

    // Extract the three data types (single currency now)
    const monthKeys = this.datasetsValue.month_keys || []
    const revenueData = this.datasetsValue.revenue || {}
    const expensesData = this.datasetsValue.expenses || {}
    const profitData = this.datasetsValue.profit || {}
    const forecastObj = this.datasetsValue.forecast

    // Helper function to get data using month keys directly
    const getDataForMonthKeys = (dataSource) => {
      return monthKeys.map(monthKey => dataSource[monthKey] || 0)
    }

    // Builds a two-point forecast series anchored to the previous month's
    // actual value. Returns null if the forecast payload is missing.
    const buildForecastSeries = (actualData, metricKey) => {
      if (!forecastObj || forecastObj[metricKey] == null) return null
      const currentKey = forecastObj.current_month_key
      const currentIdx = monthKeys.indexOf(currentKey)
      if (currentIdx < 0) return null

      const series = new Array(monthKeys.length).fill(null)
      series[currentIdx] = forecastObj[metricKey]
      if (currentIdx > 0) {
        const prevKey = monthKeys[currentIdx - 1]
        series[currentIdx - 1] = actualData[prevKey] || 0
      }
      return series
    }

    const forecastDatasetOptions = (label, color) => ({
      label: label,
      type: 'line',
      borderColor: color,
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderDash: [5, 5],
      pointRadius: 4,
      pointHoverRadius: 6,
      pointStyle: 'rectRot',
      tension: 0.3,
      fill: false,
      spanGaps: false,
      order: 0
    })

    const currency = this.currencyValue || 'GEL'
    const names = this.namesValue

    // Build datasets array (single currency). Expenses and profit are drawn
    // only when the payload has them, so a page can chart revenue alone.
    const datasets = [
      // Revenue dataset (green bars)
      {
        label: `${names.revenue} (${currency})`,
        data: getDataForMonthKeys(revenueData),
        type: 'bar',
        backgroundColor: 'rgba(32, 201, 151, 0.8)',
        borderColor: 'rgb(32, 201, 151)',
        borderWidth: 1,
        order: 2
      }
    ]
    if (this.datasetsValue.expenses) {
      datasets.push({
        label: `${names.expenses} (${currency})`,
        data: getDataForMonthKeys(expensesData),
        type: 'bar',
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderColor: 'rgb(239, 68, 68)',
        borderWidth: 1,
        order: 3
      })
    }
    if (this.datasetsValue.profit) {
      datasets.push({
        label: `${names.profit} (${currency})`,
        data: getDataForMonthKeys(profitData),
        type: 'line',
        borderColor: 'rgb(98, 125, 152)',
        backgroundColor: 'rgba(98, 125, 152, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        order: 1
      })
    }

    const forecastLabel = forecastObj && forecastObj.label ? forecastObj.label : 'Forecast'

    const forecastRevenue = buildForecastSeries(revenueData, 'revenue')
    if (forecastRevenue) {
      datasets.push({
        ...forecastDatasetOptions(`${forecastLabel} · ${names.revenue} (${currency})`, 'rgb(32, 201, 151)'),
        data: forecastRevenue
      })
    }

    this.chart = new this.Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.labelsValue,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || ''
                if (label) {
                  label += ': '
                }
                if (context.parsed.y !== null) {
                  label += context.parsed.y.toFixed(2)
                }
                return label
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return value.toFixed(0)
              }
            }
          }
        }
      }
    })
  }
}
